<template>
    <div>
        <div class="background--paper divider--container py-5 md:py-8">
            <div class="divider-white--top"></div>

            <div class="background--paper divider--container block md:hidden">
                <div class="divider-white--bottom"></div>
            </div>

            <div class="max-w-full lg:flex lg:container lg:mx-auto lg:max-w-4xl lg:pl-16 lg:pr-12">
                <div v-if="!hideCardSelection" class="container mx-auto text-center max-w-lg lg:max-w-md lg:pr-3">
                    <div class="md:border md:border-black bg-white">
                        <div class="px-3 pt-2 pb-0">
                            <h2 class="pt-3 pb-0" v-text="$t('PAYMENT_USE')"/>
                        </div>

                        <div class="input__checkbox text-left mx-auto py-5 px-8">
                            <input id="sameCard" type="checkbox" class="input__checkbox-item" v-model="sameCard">
                            <label for="sameCard" class="input__checkbox-label"
                                   v-html="$t('PAYMENT_YES', {
                                       'cardEnding': $t(`${cardEnding}`) })">
                            </label>
                        </div>
                        <div v-if="!sameCard" class="md:px-10">
                            <h2 v-text="$t('PAYMENT_DIFFERENT_CARD')"/>
                            <edit-card-details-form-new
                                @same-card-toggle="sameCard = !sameCard"
                                :billing-address="billingAddress"
                                @new-card-token="stripeToken"
                                :isGifting="true"/>
                        </div>
                        <!-- <hr class="gifting-payment--hr-line">
                        <div class="gifting-payment--coupon-section">
                            <coupon-section class="gifting-payment--coupon-section--class" />
                        </div> -->
                    </div>
                </div>

                <div class="background--paper divider--container h-5 block md:hidden">
                    <div class="divider-white--top"></div>
                    <div class="divider-white--bottom"></div>
                </div>

                <payment-summary/>

                <div class="background--paper divider--container block md:hidden">
                    <div class="divider-white--top"></div>
                </div>
            </div>

            <p class="error my-2" v-text="error" v-if="error"></p>
            <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
                <ul class="error list-disc text-center">
                    <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                        <span class="font-bold capitalize" v-text="key"/>:
                        <span class="font-normal" v-text="errorMsg"/>
                    </li>
                </ul>
            </div>

            <div class="divider-white--bottom"></div>
        </div>

        <div class="container mx-auto text-center max-w-lg py-10">
            <submit-button class="btn btn--primary" @click="submit" :processing="isFormProcessing" >
                {{$t('PAYMENT_CONFIRM')}}
            </submit-button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { CSPageEvents } from '@/helpers/GAHelpers';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import GiftingService from '@/services/gifting/GiftingService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import PaymentSummary from '@/components/partials/account/gifting/PaymentSummary';
import { PRODUCT_TYPE_SINGLE, PRODUCT_TYPE_MULTI } from '@/constants/Gifting';
import EditCardDetailsFormNew from '@/components/forms/account/EditCardDetailsFormNew';
import CouponSection from '@/components/forms/fields/CouponSection';

export default {
    name: 'Payment',
    components: { SubmitButton, PaymentSummary, EditCardDetailsFormNew, CouponSection },
    data() {
        return {
            sameCard: true,
            giftingService: new GiftingService(),
            cardToken: null,
            cardTokenRequested: false,
            formValidations: {
                cardToken: true,
                coupon: true,
            },
            errorHandler: new ErrorResponseHandler(),
            isFormProcessing: false,
            error: null,
        };
    },
    computed: {
        ...mapGetters({
            additionalProducts: 'gifting/getAdditionalProducts',
            deliveryDay: 'gifting/getDeliveryDateSelected',
            giftingAddress: 'address/getSelectedGiftingAddress',
            cardEnding: 'payment/getCardEnding',
            email: 'account/getEmail',
            fullName: 'account/getFullName',
            giftMessage: 'gifting/getGiftMessage',
            mobileNumber: 'gifting/getNotifierNumber',
            numberOfDeliveries: 'gifting/getNumberOfDeliveriesForSubscription',
            paidVase: 'gifting/getPaidVasePrice',
            isSubscription: 'gifting/isSubscription',
            frequency: 'gifting/getFrequencyForSubscription',
            includePaidVase: 'gifting/isPaidVaseAttached',
            deliveryType: 'gifting/getDeliveryType',
            singleCoupon: 'gifting/getSingleGiftCoupon',
            selectedBoxName: 'gifting/getSelectedBoxName',
            totalDeliveriesPrice: 'gifting/getTotalPriceForDeliveries',
            selectedOccassion: 'specialDates/getSelectedOccasion',
            defaultProductFamily: 'register/getDefaultProductFamily',
        }),

        billingAddress() {
            return this.$store.getters['payment/getBillingAddress'] || {};
        },

        hideCardSelection() {
            const isOneOffDelivery = this.deliveryType === 'one-off';
            const isFullyDiscounted = this.singleCoupon?.discount === 100 && this.singleCoupon?.discountType === 'percentage';
            return isOneOffDelivery && isFullyDiscounted;
        },
    },
    created() {
        this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
    },
    methods: {
        ...mapActions({
            fetchStripeSetupIntent: 'account/fetchStripeSetupIntent',
        }),
        prepareParameters() {
            let payload = {
                delivery_day: formatDate(this.deliveryDay, Dates.DASHED_YEAR_MONTH_DAY),
                gifting_address_id: this.giftingAddress.id,
                include_message: true,
                is_multiple_deliveries: this.isSubscription,
                message_body: this.giftMessage.body,
                message_from: this.giftMessage.from,
                message_to: this.giftMessage.to,
                mobile_number: this.mobileNumber,
                number_of_deliveries: this.numberOfDeliveries,
                paid_vase: (this.isFreeVase && !this.isSubscription && this.selectedBoxName === PRODUCT_TYPE_SINGLE) || this.includePaidVase,
            };
            if (this.selectedBoxName !== PRODUCT_TYPE_SINGLE && this.selectedBoxName !== PRODUCT_TYPE_MULTI) {
                payload.primary_product_id = this.$store.getters['gifting/getProductTypeIdFromName'](this.selectedBoxName);
            }
            if (this.isSubscription) {
                payload.frequency = this.frequency;
            }
            if (this.cardToken) {
                payload.stripe_token = this.cardToken;
            }

            if (this.selectedOccassion) {
                payload.occasion_id = this.selectedOccassion?.id;
            }

            if (this.additionalProducts.length) {
                payload.products = [];
                this.additionalProducts.forEach(product => {
                    payload.products.push({
                        product_id: product.productId,
                        quantity: product.quantity,
                    });
                });
            }
            return payload;
        },
        submit() {
            if (this.sameCard && !this.isFormProcessing) {
                this.toggleFormProcessing();
                this.processGiftViaApi();
            } else if (!this.sameCard && !this.isFormProcessing) {
                this.toggleFormProcessing();
                if (process.env.VUE_APP_3DS_ENABLED) {
                    this.$refs.cardForm.handleScaCardSubmission()
                        .then(tokenData => {
                            this.cardToken = tokenData.setupIntentId;
                            this.processGiftViaApi();
                        })
                        .catch(() => {
                            this.handleFail();
                        });
                } else {
                    this.$refs.cardForm.handleFetchingToken()
                        .then(tokenData => {
                            this.cardToken = tokenData.tokenId;
                            this.processGiftViaApi();
                        })
                        .catch(() => {
                            this.handleFail();
                        });
                }
            }
            CSPageEvents('giftOneOff', {
                productType: 'gift',
                productFamily: this.selectedBoxName || this.defaultProductFamily,
                frequency: 'once',
            });
        },
        handleFail() {
            this.processing = false;
            this.$scrollTo(`.input--invalid`, {
                offset: -100,
            });
        },
        processGiftViaApi() {
            this.giftingService.createGift(this.prepareParameters())
                .then(response => this.handleSuccessfulResponse(response))
                .catch(error => {
                    this.toggleFormProcessing();
                    this.$store.commit('account/setSetupIntent', null);
                    this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
                    const hasAmex = error.response?.data?.error?.includes('Amex');
                    if (hasAmex) return this.error = this.$t('AMERICAN_EXPRESS_NOT_SUPPORTED');
                    this.errorHandler.setResponse(error.response.data);
                });
        },
        handleSuccessfulResponse(response) {
            this.$store.commit('gifting/setGiftProcessed', true);
            this.$store.commit('gifting/setGiftReference', response.data.properties.reference);
            this.$store.commit('gifting/setGiftNoteUrl', response.data.properties.giftNoteUrl);
            this.$router.push({ name: 'account.gifting.thank-you' });
        },
        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },
        stripeToken(param) {
            this.cardToken = param;
        },
    },
};
</script>

<style lang="scss" scoped>
.gifting-payment {
    &--coupon-section {
        text-align: left;
        padding-left: 2rem;
        margin: 25px 0;
        &--class {
            margin: 0;
            padding-left: 0;
        }
    }
    &--hr-line {
        width: 90%;
        margin: 0 auto;
    }
}
</style>
